<template>
  <div class="feedback-submit-form">
    <p v-if="requesterName" class="requester-note mb-2">
      <strong>Feedback requested by:</strong> {{ requesterName }}
    </p>

    <div class="preview-section mb-3">
      <div class="preview-header">
        <p class="mb-0"><strong>Entry preview</strong></p>
        <div class="preview-actions">
          <b-button size="sm" variant="outline-info" @click="openPreview" v-if="pdfUrl">Open PDF</b-button>
          <b-button size="sm" variant="info" @click="print" v-if="pdfUrl">Print</b-button>
        </div>
      </div>

      <b-alert v-if="pdfError" show variant="warning" class="mt-2 mb-0">
        Unable to load the PDF preview. You can still submit feedback.
      </b-alert>

      <pdf
        v-if="pdfUrl && !pdfError"
        class="mt-2"
        :resize="true"
        :src="pdfUrl"
        :page="currentPage"
        @numpages="updatePageCount"
        @error="onPdfError"
      >
        <template slot="loading">
          <Pulse />
        </template>
      </pdf>

      <div v-if="pdfUrl && !pdfError" class="pdf-nav mt-2">
        <b-button size="sm" variant="outline-secondary" :disabled="!canMoveBack" @click="previous">Previous</b-button>
        <span class="pdf-page">Page {{ currentPage }} / {{ numPages || 1 }}</span>
        <b-button size="sm" variant="outline-secondary" :disabled="!canMoveForward" @click="next">Next</b-button>
      </div>
    </div>

    <b-form @submit.prevent="submit">
      <b-form-group label="Reviewer Name" label-for="reviewer-name" required>
        <b-form-input id="reviewer-name" v-model.trim="name" :state="nameState" />
      </b-form-group>

      <b-form-group label="Reviewer Role / Title" label-for="reviewer-role">
        <b-form-input id="reviewer-role" v-model.trim="role" />
      </b-form-group>

      <b-form-group label="Category" label-for="feedback-category" required>
        <b-form-select
          id="feedback-category"
          v-model="category"
          :options="categoryOptions"
          :state="categoryState"
        />
      </b-form-group>

      <b-form-group label="Feedback" label-for="feedback" required>
        <b-form-textarea id="feedback" v-model.trim="feedback" :state="feedbackState" rows="6" />
      </b-form-group>

      <b-form-group label="Attachment(s)" description="You may upload evidence files (max 10MB each).">
        <FeedbackFileUpload v-model="uploadedFiles" :request-id="requestId" />
      </b-form-group>

      <b-form-group label="Signature" required>
        <SignaturePad :modelValue="signature" @update:modelValue="signature = $event" :width="734" :height="178" />
      </b-form-group>

      <b-button type="submit" variant="info" :disabled="!formState || submitting">Submit Feedback</b-button>
    </b-form>
  </div>
</template>

<script>
import FeedbackFileUpload from "@/components/entries/FeedbackFileUpload.vue";
import SignaturePad from "@/components/form/SignaturePad.vue";
import Pulse from "@/components/loaders/Pulse";

export default {
  name: "FeedbackSubmitForm",
  props: {
    requestId: { type: String, required: true },
    prefillReviewerName: { type: String, default: "" },
    prefillReviewerRole: { type: String, default: "" },
    requesterName: { type: String, default: "" },
  },
  components: {
    FeedbackFileUpload,
    SignaturePad,
    pdf: () => import("pdfvuer"),
    Pulse,
  },
  data() {
    return {
      category: "",
      categoryOptions: [
        { value: "", text: "Select category" },
        { value: "Case Based Discussion", text: "Case Based Discussion" },
        { value: "Mini CEX", text: "Mini CEX" },
        { value: "Supervision", text: "Supervision" },
        { value: "CPD Feedback", text: "CPD Feedback" },
        { value: "Observed Practice", text: "Observed Practice" },
        { value: "Complaints and Compliments", text: "Complaints and Compliments" },
        { value: "Feedback from Colleagues", text: "Feedback from Colleagues" },
        { value: "Feedback from Patients", text: "Feedback from Patients" },
        { value: "Quality Improvement Activity", text: "Quality Improvement Activity" },
        { value: "Significant Event", text: "Significant Event" },
        { value: "Friends & Family", text: "Friends & Family" },
      ],
      name: this.prefillReviewerName || "",
      role: this.prefillReviewerRole || "",
      feedback: "",
      signature: "",
      submitting: false,
      apiRoot: "",
      pdfError: false,
      numPages: 0,
      currentPage: 1,
      uploadedFiles: [],
    };
  },
  computed: {
    pdfUrl() {
      if (!this.requestId) return null;
      return this.apiRoot + `entries/feedback/pdf?id=${encodeURIComponent(this.requestId)}`;
    },
    categoryState() {
      return this.category.trim().length > 0;
    },
    nameState() {
      return this.name.trim().length > 0;
    },
    feedbackState() {
      return this.feedback.trim().length > 0;
    },
    signatureState() {
      return this.signature && this.signature.length > 0;
    },
    formState() {
      return this.categoryState && this.nameState && this.feedbackState && this.signatureState;
    },
    fileIds() {
      return (this.uploadedFiles || []).map((v) => v.id);
    },
    canMoveForward() {
      return this.currentPage < this.numPages;
    },
    canMoveBack() {
      return this.currentPage > 1;
    },
  },
  methods: {
    openPreview() {
      if (this.pdfUrl) window.open(this.pdfUrl, "_blank");
    },
    print() {
      if (this.pdfUrl) this.$printUtils.printPDF(this.pdfUrl);
    },
    updatePageCount(pageCount) {
      this.numPages = pageCount;
    },
    next() {
      this.currentPage++;
    },
    previous() {
      this.currentPage--;
    },
    onPdfError() {
      this.pdfError = true;
    },
    submit() {
      if (!this.formState) return;
      this.submitting = true;

      const payload = {
        request_id: this.requestId,
        category: this.category,
        name: this.name,
        role: this.role || null,
        feedback: this.feedback,
        signature: this.signature,
        file_ids: this.fileIds,
      };

      this.$api.v2
        .post("entries/feedback/submit", payload)
        .then((resp) => {
          this.$success("Submitted", resp.body ? resp.body.message : "Feedback submitted");
          this.$emit("submitted");
        })
        .catch((resp) => {
          this.$httpError("Failed to submit feedback", resp);
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },
  created() {
    this.apiRoot = process.env.VUE_APP_API_V2_ROOT;
  },
};
</script>

<style scoped>
.requester-note {
  color: #2c3e50;
}

.preview-section { width: 100%; }
.preview-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.preview-actions { display: flex; gap: 8px; }
.pdf-nav { display:flex; align-items:center; justify-content: space-between; gap: 12px; }
.pdf-page { color: #6c757d; font-size: 12px; }
</style>
